import APITemplate from '@api/APITemplate'
import con from '@services/connection'
import store from '@state/store'
import { MINUTES_TIMEOUT_REQUESTS } from '@constants/Numbers'
import { resolveConsolidationScope } from '@modules/consolidator/utils/consolidation-scope/consolidation-scope'
import { formatQueryParams, safeJsonParse } from '@utils/Format'
import {
  claimLatestRequest,
  buildRequestKey,
  dedupeInFlight,
  isSameScope,
  shouldUseCachedData,
} from '@modules/consolidator/utils/Api/Api'
import { adaptStatisticsPerAssetResponse } from '@modules/consolidator/utils/adapt-statistics-per-asset-response/adapt-statistics-per-asset-response'
import { serializeWalletAnalysisParams } from '@modules/consolidator/utils/serialize-wallet-analysis-params/serialize-wallet-analysis-params'

const FIXED_GROUP_BY = 'id_dado_ativo'
const FIXED_DATA_REQUESTED = ['statistics']

class APIConsolidatorStatisticsPerAsset extends APITemplate {
  constructor() {
    super()

    this.statisticsPerAssetRegistry = {}
    this.statisticsPerAssetRequests = {}
  }

  carregar({ id_cliente_app, is_client, forceUpdate, ...filters } = {}) {
    const escopo = resolveConsolidationScope({
      isClient: is_client,
      idClienteApp: id_cliente_app,
    })

    const { error, params } = serializeWalletAnalysisParams({
      id_cliente_app,
      is_client: escopo.allowRequestWithoutClient,
      ...filters,
      group_by: FIXED_GROUP_BY,
      data_requested: FIXED_DATA_REQUESTED,
    })

    if (error) {
      return adaptStatisticsPerAssetResponse({
        status: 400,
        value: { status: 400, message: error },
      })
    }

    const scopeKey = escopo.scopeKey
    const cached = this.getCachedResponse({ forceUpdate, scopeKey })
    if (cached) return cached

    return dedupeInFlight({
      registry: this.statisticsPerAssetRegistry,
      key: buildRequestKey({ scopeKey, params }),
      run: () =>
        this.fetchStatisticsPerAsset({
          url: escopo.url,
          params,
          scopeKey,
        }),
    })
  }

  async fetchStatisticsPerAsset({ url: baseUrl, params, scopeKey }) {
    const request = claimLatestRequest(this.statisticsPerAssetRequests)
    store.dispatch('consolidator/setStatisticsPerAssetLoading', true)

    const url = baseUrl + formatQueryParams(params)

    try {
      const adapted = await con
        .get(url)
        .then(safeJsonParse)
        .then(adaptStatisticsPerAssetResponse)

      if (request.isStale()) {
        return store.getters['consolidator/statisticsPerAsset']
      }

      this.dispatchAdapted(adapted, scopeKey)

      return adapted
    } finally {
      if (!request.isStale()) {
        store.dispatch('consolidator/setStatisticsPerAssetLoading', false)
      }
    }
  }

  getCachedResponse({ forceUpdate, scopeKey }) {
    if (
      !isSameScope(
        store.getters['consolidator/statisticsPerAssetClientId'],
        scopeKey
      )
    ) {
      return null
    }

    if (
      shouldUseCachedData({
        forceUpdate,
        lastUpdate: store.getters['consolidator/statisticsPerAssetLastUpdate'],
        timeoutMinutes: MINUTES_TIMEOUT_REQUESTS,
      })
    ) {
      return store.getters['consolidator/statisticsPerAsset']
    }

    if (store.getters['consolidator/statisticsPerAssetLoading']) {
      return store.getters['consolidator/statisticsPerAsset']
    }

    return null
  }

  dispatchAdapted(adapted, scopeKey) {
    const data = adapted && adapted.ok ? adapted.statistics_by_asset || {} : {}
    store.dispatch('consolidator/setStatisticsPerAsset', {
      data,
      clientId: scopeKey,
    })
  }
}

const apiConsolidatorStatisticsPerAsset =
  new APIConsolidatorStatisticsPerAsset()
export { apiConsolidatorStatisticsPerAsset }
